import { IconChevronCompactDown } from '@tabler/icons-react-native'
import { useCallback, useState, type FC } from 'react'

import {
  HStack,
  Icon,
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
  Pressable,
  PressableProps,
  Text
} from '@/components'
import { COLOR_WHITE } from '@/lib'

export interface PhotoCarouselCaptionProps {
  description: string
  width: number
}

const maxLines = 3

export const PhotoCarouselCaption: FC<PhotoCarouselCaptionProps> = ({ description, width }) => {
  const [clampedHeight, setClampedHeight] = useState(0)
  const [unclampedHeight, setUnclampedHeight] = useState(0)

  return (
    <Popover
      aria-hidden
      isKeyboardDismissable
      shouldOverlapWithTrigger
      trigger={useCallback(
        (triggerProps: PressableProps) => (
          <Pressable
            {...triggerProps}
            accessible={false}
            accessibilityElementsHidden
            aria-hidden
            className='absolute bottom-0 left-0 right-0 h-fit flex-col items-center justify-center bg-brand-black-500/80 px-3 py-2'
            importantForAccessibility='no-hide-descendants'
          >
            <Text
              accessible={false}
              accessibilityElementsHidden
              aria-hidden
              className='text-center text-sm text-white'
              ellipsizeMode='tail'
              importantForAccessibility='no-hide-descendants'
              numberOfLines={maxLines}
              onLayout={(event) => setClampedHeight(event.nativeEvent.layout.height)}
            >
              {description}
            </Text>
            <Text // this is a non-visible unclamped text element, so we can see the true height of the text
              accessible={false}
              accessibilityElementsHidden
              aria-hidden
              className='absolute text-center text-sm text-white opacity-0'
              ellipsizeMode='tail'
              importantForAccessibility='no-hide-descendants'
              onLayout={(event) => setUnclampedHeight(event.nativeEvent.layout.height)}
              pointerEvents='none'
            >
              {description}
            </Text>
            {unclampedHeight > clampedHeight ? (
              <HStack
                accessible={false}
                accessibilityElementsHidden
                aria-hidden
                className='items-center justify-center'
                importantForAccessibility='no-hide-descendants'
              >
                <Icon
                  as={IconChevronCompactDown}
                  size='xs'
                  stroke={COLOR_WHITE}
                  strokeWidth={2.5}
                />
              </HStack>
            ) : null}
          </Pressable>
        ),
        [clampedHeight, description, unclampedHeight]
      )}
    >
      <PopoverBackdrop />
      <PopoverContent
        accessible={false}
        accessibilityElementsHidden
        aria-hidden
        className='items-center justify-center border-0 bg-brand-black-500 px-3 py-2'
        importantForAccessibility='no-hide-descendants'
        style={{
          maxWidth: width
        }}
      >
        <PopoverBody>
          <Text
            accessible={false}
            accessibilityElementsHidden
            aria-hidden
            className='text-center text-sm text-white'
            importantForAccessibility='no-hide-descendants'
          >
            {description}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
